/**
 * The statistics strip. Every figure states what it counted over: a number
 * without its denominator is the thing this product exists not to publish.
 */

import type { PricingStatistics } from "@pricing/contracts";

export function StatsStrip({
  statistics,
}: {
  readonly statistics: PricingStatistics | null;
}): React.JSX.Element {
  if (!statistics) {
    return <p className="notice">No statistics yet: nothing has been collected.</p>;
  }
  const covered = statistics.offerings_covered;
  return (
    <section aria-label="Statistics for the selected period" className="stats">
      <div className="stat">
        <div className="stat__value">{covered.covered}</div>
        <div className="stat__label">Offerings covered</div>
        <div className="stat__denominator">
          of {covered.total_matching} matching · {covered.complete} complete, {covered.partial} partial,{" "}
          {covered.uncovered} uncovered
        </div>
      </div>
      <div className="stat">
        <div className="stat__value">{statistics.offerings_changed}</div>
        <div className="stat__label">Offerings changed</div>
        <div className="stat__denominator">
          deduplicated across sources and buckets
        </div>
      </div>
      <div className="stat">
        <div className="stat__value">
          {statistics.increases} / {statistics.decreases}
        </div>
        <div className="stat__label">Increases / decreases</div>
        <div className="stat__denominator">source-specific bucket events</div>
      </div>
      <div className="stat">
        <div className="stat__value">{statistics.newly_observed}</div>
        <div className="stat__label">Newly observed</div>
        <div className="stat__denominator">first observation in period, not a launch date</div>
      </div>
      <div className="stat">
        <div className="stat__value">{statistics.stale_sources.count}</div>
        <div className="stat__label">Stale sources</div>
        <div className="stat__denominator">
          {statistics.stale_sources.stale_unit_count} coverage unit(s):{" "}
          {statistics.stale_sources.source_ids.join(", ") || "none"}
        </div>
      </div>
      <div className="stat">
        <div className="stat__value">{covered.fresh} fresh</div>
        <div className="stat__label">Freshness at cutoff</div>
        <div className="stat__denominator">
          {covered.stale} stale · excluded: {statistics.excluded.coverage_events} coverage,{" "}
          {statistics.excluded.corrections} corrections
        </div>
      </div>
    </section>
  );
}
